import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import {
  apiKeySuffix,
  maskApiKey,
  VISIBLE_SUFFIX_LENGTH,
} from "@/pages/applications/pages/apiKeys/utils/maskApiKey";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";

type ApiKey = InterludeTypes["ApiKey"];

interface FormValues {
  confirmValue: string;
}

interface RevokeApiKeyModalProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onSuccess: () => void;
}

const styles = {
  warningList: {
    margin: 0,
    paddingLeft: "20px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/**
 * What the reader has to type back before the button unlocks.
 *
 * The name, when there is one -- that is the thing they recognize the key by.
 * When there is not, the visible tail of the key: an unnamed key is exactly
 * the case where copying the reference modal's "type the name" check would
 * make an irreversible action require typing nothing at all, because an empty
 * field already equals an empty name.
 */
function confirmationTarget(apiKey: ApiKey): {
  value: string;
  isName: boolean;
} {
  const name = apiKey.name.trim();
  if (name.length > 0) return { value: name, isName: true };
  return { value: apiKeySuffix(apiKey.key), isName: false };
}

export const RevokeApiKeyModal: React.FC<RevokeApiKeyModalProps> =
  function RevokeApiKeyModal({ open, apiKey, onClose, onSuccess }) {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, watch } = useForm<FormValues>({
      mode: "onChange",
      defaultValues: { confirmValue: "" },
    });

    const watchedConfirmValue = watch("confirmValue");

    useEffect(() => {
      if (apiKey) {
        reset({ confirmValue: "" });
      }
    }, [apiKey, reset]);

    const deleteMutation = interlude.apiKey.useDeleteApiKey(
      { params: { credentialId: apiKey?.id ?? "" } },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        /**
         * No `onError`. Every way this endpoint refuses -- 403
         * `OPENAPI_V2_NOT_ENABLED`, 404 `OPENAPI_CREDENTIAL_NOT_FOUND`, 502
         * `OPENAPI_KONG_UNAVAILABLE` -- is now a code the global handler has a
         * message for, and revoke sends no field for a message to sit beside:
         * it posts no body at all, so the codeless 422 the create and rename
         * forms have to answer for cannot reach it.
         */
      },
    );

    const handleClose = useHandler(() => {
      reset({ confirmValue: "" });
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler(() => {
      if (!apiKey) return;
      deleteMutation.mutate(undefined);
    });

    const target = useMemo(
      () => (apiKey ? confirmationTarget(apiKey) : null),
      [apiKey],
    );

    const isConfirmed = useMemo(() => {
      if (!target) return false;
      return watchedConfirmValue.trim() === target.value;
    }, [watchedConfirmValue, target]);

    const footer: React.ReactNode = useMemo(
      () => (
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={handleClose}>
            {t("apiKey.revokeModal.cancel", "Cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit(onSubmit)}
            disabled={!isConfirmed || !apiKey || deleteMutation.isPending}
            loading={deleteMutation.isPending}
          >
            {t("apiKey.revokeModal.submit", "Revoke key")}
          </Button>
        </Stack>
      ),
      [
        apiKey,
        handleClose,
        handleSubmit,
        onSubmit,
        isConfirmed,
        deleteMutation.isPending,
        t,
      ],
    );

    if (!apiKey || !target) {
      return null;
    }

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("apiKey.revokeModal.title", "Revoke API key")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t(
              "apiKey.revokeModal.description",
              "Revoking deletes this key from the API gateway.",
            )}
          </Typography>

          <InfoBox
            variant="warning"
            title={t(
              "apiKey.revokeModal.warning.title",
              "This cannot be undone",
            )}
            description={
              <Box component="ul" sx={styles.warningList}>
                <li>
                  <Typography variant="body" color="inherit">
                    {t(
                      "apiKey.revokeModal.warning.immediate",
                      "Any system still calling the API with this key stops working immediately.",
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body" color="inherit">
                    {/*
                     * Not softened. Interlude deletes the credential in Kong
                     * outright, leaving no record to restore from -- unlike
                     * archiving a custom field, there is no later path back.
                     */}
                    {t(
                      "apiKey.revokeModal.warning.irreversible",
                      "The key is deleted outright. It cannot be restored, and nothing is kept to look it up by later.",
                    )}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body" color="inherit">
                    {t(
                      "apiKey.revokeModal.warning.alternative",
                      "To stop a key from working while keeping it, disable it instead.",
                    )}
                  </Typography>
                </li>
              </Box>
            }
          />

          <Box sx={sharedStyles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("apiKey.revokeModal.keyLabel", "Key")}
            </Typography>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {maskApiKey(apiKey.key)}
            </Typography>
          </Box>

          <Box sx={sharedStyles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {target.isName
                ? t(
                    "apiKey.revokeModal.confirmLabel.name",
                    "To confirm, type the key's name: {{confirmValue}}",
                    { confirmValue: target.value },
                  )
                : t(
                    "apiKey.revokeModal.confirmLabel.keySuffix",
                    "This key has no name, so type the {{characterCount}} characters shown in the list instead: {{confirmValue}}",
                    {
                      // Not `count`: i18next reads that as a plural selector
                      // and would look for `..._one` / `..._other` keys.
                      characterCount: VISIBLE_SUFFIX_LENGTH,
                      confirmValue: target.value,
                    },
                  )}
            </Typography>
            <Controller
              name="confirmValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  data-test="revoke-confirm-input"
                  placeholder={target.value}
                  disabled={deleteMutation.isPending}
                />
              )}
            />
          </Box>
        </Stack>
      </Modal>
    );
  };
